import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Import components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Home from './components/Home';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/tickets/Dashboard';
import TicketDetails from './components/tickets/TicketDetails';
import CreateTicket from './components/tickets/CreateTicket';
import AdminDashboard from './components/admin/AdminDashboard';
import ChangePassword from './components/auth/ChangePassword';
import ProfilePage from './components/auth/ProfilePage';

// Context providers
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './components/auth/PrivateRoute';
import AdminRoute from './components/auth/AdminRoute';

// Extend Window interface to include our custom property
declare global {
  interface Window {
    _lastToggleTime?: number;
  }
}

// Create theme implementation with system preference detection
const ThemeContext = React.createContext({
  theme: 'light',
  toggleTheme: () => {}
});

function App() {
  // Get system color scheme preference
  const getSystemTheme = () => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? 'dark' 
      : 'light';
  };

  const [theme, setTheme] = useState(() => {
    // Use stored theme if available, otherwise use system preference
    return localStorage.getItem('theme') || getSystemTheme();
  });

  useEffect(() => {
    // Update theme when it changes
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Add listener for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // Update theme when system preference changes if user hasn't explicitly set a preference
    const handleChange = () => {
      if (!localStorage.getItem('theme')) {
        setTheme(getSystemTheme());
      }
    };

    // Add listener for system theme changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    // Clean up listener
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const toggleTheme = () => {
    // If we're currently using system theme, set explicit preference
    if (!localStorage.getItem('theme')) {
      const newTheme = theme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme); // Save explicit user preference
      setTheme(newTheme);
    } 
    // If triple-clicked (detected by rapid toggle within 500ms), reset to system preference
    else if (Date.now() - (window._lastToggleTime || 0) < 500) {
      localStorage.removeItem('theme'); // Remove saved preference
      setTheme(getSystemTheme()); // Use system preference
      window._lastToggleTime = 0; // Reset time tracking
    } 
    // Otherwise just toggle between light/dark
    else {
      setTheme(prevTheme => {
        const newTheme = prevTheme === 'light' ? 'dark' : 'light';
        localStorage.setItem('theme', newTheme); // Save explicit user preference
        return newTheme;
      });
      // Track time of last toggle for triple-click detection
      window._lastToggleTime = Date.now();
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <AuthProvider>
        <Router>
          <div className="App d-flex flex-column min-vh-100">
            <Navbar />
            <main className="container py-4 flex-grow-1">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route 
                  path="/dashboard" 
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/tickets/new" 
                  element={
                    <PrivateRoute>
                      <CreateTicket />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/tickets/:id" 
                  element={
                    <PrivateRoute>
                      <TicketDetails />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/profile" 
                  element={
                    <PrivateRoute>
                      <ProfilePage />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/profile/change-password" 
                  element={
                    <PrivateRoute>
                      <ChangePassword />
                    </PrivateRoute>
                  } 
                />
                <Route 
                  path="/admin" 
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  } 
                />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </main>
            <Footer />
          </div>
        </Router>
      </AuthProvider>
    </ThemeContext.Provider>
  );
}

// Export the ThemeContext for use in Navbar
export const useTheme = () => React.useContext(ThemeContext);
export default App;
